import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  FiActivity,
  FiBook,
  FiDownload,
  FiFilm,
  FiGrid,
  FiHeart,
  FiImage,
  FiMoreHorizontal,
  FiShoppingBag,
  FiShoppingCart,
  FiTruck,
  FiUsers,
} from 'react-icons/fi';
import { FaGavel } from 'react-icons/fa';
import { Card } from '@/components';
import { apiJson, ApiError } from '@/lib/api';
import { downloadProfessionalSectionExport } from '@/lib/pro-client-export';
import { formatMoneyAbsolute, formatShortDate } from '@/lib/utils';
import type {
  AccountingSummary,
  ExpenseCategory,
  ExpenseCategoryListResponse,
  ExpenseListResponse,
  PaymentListResponse,
  PaymentMethod,
  PaymentMethodListResponse,
  ProfessionalClientListResponse,
  UnifiedItem,
} from '@/lib/types';

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  general: FiGrid,
  school: FiBook,
  medical: FiHeart,
  clothing: FiShoppingBag,
  activities: FiActivity,
  groceries: FiShoppingCart,
  transportation: FiTruck,
  childcare: FiUsers,
  entertainment: FiFilm,
  other: FiMoreHorizontal,
};

function ProClientExpensesPage() {
  const { inviteId } = Route.useParams();
  const id = Number(inviteId);
  const { getToken } = useAuth();
  const [exportError, setExportError] = useState<string | null>(null);

  const clientsQuery = useQuery({
    queryKey: ['professional-clients'],
    queryFn: () => apiJson<ProfessionalClientListResponse>(getToken, '/v1/professional/clients'),
  });
  const invite = useMemo(
    () => clientsQuery.data?.clients.find((c) => c.id === id),
    [clientsQuery.data, id]
  );
  const coparentId = invite?.coparent_user_id;

  const canView = !!invite?.share_expenses;

  const summaryQuery = useQuery({
    queryKey: ['professional-client-accounting', id],
    queryFn: () =>
      apiJson<AccountingSummary>(getToken, `/v1/professional/clients/${id}/accounting/summary`),
    enabled: canView,
  });

  const expensesQuery = useQuery({
    queryKey: ['professional-client-expenses', id],
    queryFn: () =>
      apiJson<ExpenseListResponse>(getToken, `/v1/professional/clients/${id}/expenses`),
    enabled: canView,
  });

  const paymentsQuery = useQuery({
    queryKey: ['professional-client-payments', id],
    queryFn: () =>
      apiJson<PaymentListResponse>(getToken, `/v1/professional/clients/${id}/payments`),
    enabled: canView,
  });

  const categoriesQuery = useQuery({
    queryKey: ['expense-categories'],
    queryFn: () =>
      apiJson<ExpenseCategoryListResponse>(getToken, '/v1/expense-categories'),
  });

  const paymentMethodsQuery = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () =>
      apiJson<PaymentMethodListResponse>(getToken, '/v1/payment-methods'),
  });

  const owedCents = summaryQuery.data?.amount_owed_cents ?? 0;
  const expenses = expensesQuery.data?.expenses ?? [];
  const payments = paymentsQuery.data?.payments ?? [];
  const categories = categoriesQuery.data?.categories ?? [];
  const paymentMethods = paymentMethodsQuery.data?.payment_methods ?? [];

  const unifiedItems: UnifiedItem[] = useMemo(
    () =>
      [
        ...expenses.map((expense) => ({ type: 'expense' as const, data: expense })),
        ...payments.map((payment) => ({ type: 'payment' as const, data: payment })),
      ].sort((a, b) => {
        const dateA = a.type === 'expense' ? a.data.purchase_date : a.data.payment_date;
        const dateB = b.type === 'expense' ? b.data.purchase_date : b.data.payment_date;
        return new Date(dateB).getTime() - new Date(dateA).getTime();
      }),
    [expenses, payments]
  );

  const isLoading =
    expensesQuery.isLoading || paymentsQuery.isLoading || categoriesQuery.isLoading;
  const isError = expensesQuery.isError || paymentsQuery.isError;

  const getCategory = (categoryId: string): ExpenseCategory | undefined =>
    categories.find((c) => c.expense_category_id === categoryId);

  const getPaymentMethod = (methodId: string): PaymentMethod | undefined =>
    paymentMethods.find((m) => m.payment_method_id === methodId);

  const getCategoryIconComponent = (categoryId: string) =>
    categoryIcons[categoryId] || FiGrid;

  const handleExport = async () => {
    setExportError(null);
    try {
      await downloadProfessionalSectionExport(getToken, id, 'accounting');
    } catch (e: unknown) {
      let msg = 'Could not download export.';
      if (e instanceof ApiError && e.bodyText) {
        try {
          const j = JSON.parse(e.bodyText) as { detail?: string };
          if (j.detail) msg = j.detail;
        } catch {
          /* ignore */
        }
      }
      setExportError(msg);
    }
  };

  const bodyStyle = {
    fontFamily:
      "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  } as const;

  return (
    <div style={bodyStyle}>
      <div className='flex items-center justify-between gap-4 mb-4'>
        <h2 className='text-lg font-semibold text-gray-900'>Expenses</h2>
        <button
          type='button'
          onClick={handleExport}
          className='flex items-center gap-2 px-3 py-2 rounded-xl border border-[#018A9D] text-sm font-semibold text-[#018A9D] hover:bg-[#018A9D]/5'
        >
          <FiDownload className='w-4 h-4' />
          Download CSV
        </button>
      </div>
      {exportError ? (
        <p className='text-sm text-red-600 mb-2'>{exportError}</p>
      ) : null}

      <Card className='text-center mb-6'>
        <p className='text-sm font-semibold text-[#018A9D]/80 mb-1'>Net balance (inviting co-parent view)</p>
        <p className='text-3xl font-bold text-gray-900'>{formatMoneyAbsolute(owedCents)}</p>
      </Card>

      <h3 className='text-base font-bold text-gray-900 mb-3'>Transactions</h3>

      {isLoading ? (
        <p className='text-gray-500 text-center py-8'>Loading…</p>
      ) : isError ? (
        <p className='text-gray-500 text-center py-8'>Couldn&apos;t load transactions.</p>
      ) : unifiedItems.length === 0 ? (
        <p className='text-gray-500 text-center py-8'>No transactions yet.</p>
      ) : (
        <div className='space-y-3'>
          {unifiedItems.map((item) => {
            if (item.type === 'expense') {
              const expense = item.data;
              const category = getCategory(expense.category);
              const categoryColor = category?.color ?? '#018B9C';
              const categoryName = category?.name ?? expense.category;
              const IconComponent = getCategoryIconComponent(expense.category);
              const hasDispute = (expense.disputes ?? []).length > 0;
              const hasAttachments = (expense.attachments ?? []).length > 0;

              const userSplit = coparentId
                ? expense.splits?.find((s) => s.split_for_user_id === coparentId)
                : undefined;
              let splitDisplay: string | undefined;
              if (expense.splits && expense.splits.length > 0 && userSplit && coparentId) {
                const userPercent = userSplit.amount_percent;
                const otherPercent = expense.splits
                  .filter((s) => s.split_for_user_id !== coparentId)
                  .reduce((sum, s) => sum + s.amount_percent, 0);
                splitDisplay = `${userPercent}% / ${otherPercent}%`;
              }

              return (
                <Card key={`expense-${expense.id}`} className='flex items-center gap-3'>
                  <div
                    className='w-10 h-10 rounded-full flex items-center justify-center relative shrink-0'
                    style={{ backgroundColor: categoryColor }}
                  >
                    <IconComponent className='w-5 h-5 text-white' />
                    {hasAttachments ? (
                      <div className='absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-[#018A9D] flex items-center justify-center border-2 border-white'>
                        <FiImage className='w-2.5 h-2.5 text-white' />
                      </div>
                    ) : null}
                  </div>
                  <div className='flex-1 min-w-0'>
                    <p className='font-bold text-gray-900 truncate'>{expense.name}</p>
                    <div className='flex items-center gap-1.5 mt-1'>
                      <p className='text-xs text-gray-400'>
                        {formatShortDate(expense.purchase_date)} • {categoryName}
                      </p>
                      {hasDispute ? <FaGavel className='w-3 h-3 text-amber-500' /> : null}
                    </div>
                  </div>
                  <div className='text-right shrink-0'>
                    <p className='font-extrabold text-gray-900'>
                      {formatMoneyAbsolute(expense.amount_cents)}
                    </p>
                    {splitDisplay ? (
                      <p className='text-xs text-gray-400 mt-1'>{splitDisplay}</p>
                    ) : null}
                  </div>
                </Card>
              );
            }

            const payment = item.data;
            const method = getPaymentMethod(payment.payment_method);
            const methodColor = method?.color ?? '#9B59B6';
            const methodName = method?.name ?? payment.payment_method;
            const hasDispute = (payment.disputes ?? []).length > 0;

            return (
              <Card key={`payment-${payment.id}`} className='flex items-center gap-3'>
                <div
                  className='w-10 h-10 rounded-full flex items-center justify-center shrink-0'
                  style={{ backgroundColor: methodColor }}
                >
                  <span className='text-white font-bold text-lg'>$</span>
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='font-bold text-gray-900'>Payment</p>
                  <div className='flex items-center gap-1.5 mt-1'>
                    <p className='text-xs text-gray-400'>
                      {formatShortDate(payment.payment_date)} • {methodName}
                    </p>
                    {hasDispute ? <FaGavel className='w-3 h-3 text-amber-500' /> : null}
                  </div>
                </div>
                <div className='text-right shrink-0'>
                  <p className='font-extrabold text-gray-900'>
                    {formatMoneyAbsolute(payment.amount_cents)}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/pro/clients/$inviteId/expenses')({
  component: ProClientExpensesPage,
});
